import { useCallback, useEffect, useRef, useState } from 'react';

export type GeoPermission = 'unknown' | 'granted' | 'denied' | 'prompt' | 'unavailable';

export interface GeoState {
  permission: GeoPermission;
  position: GeolocationPosition | null;
  error: string | null;
  loading: boolean;
}

/** דיוק גבוה (GPS) — לפעמים לא זמין בתוך מבנה/מרתף; מיקום גס (רשת/Wi-Fi) — מהיר ועובד כמעט תמיד */
const FINE: PositionOptions = { enableHighAccuracy: true, maximumAge: 5_000, timeout: 30_000 };
const COARSE: PositionOptions = { enableHighAccuracy: false, maximumAge: 20_000, timeout: 30_000 };

/** אם לא הגיע עדכון מיקום כל כך הרבה זמן (והדף גלוי) — מפעילים את המעקב מחדש (iOS נוטה "להירדם") */
const STALE_RESTART_MS = 45_000;

/**
 * בוחר בין המיקום הנוכחי שנשמר לבין עדכון חדש. עדכון חדש מחליף אם הוא מדויק בערך כמו הקודם או טוב ממנו,
 * או אם הקודם כבר ישן — כך מיקום גס שמגיע מהר לא דורס מיקום GPS מדויק שהתקבל לפני רגע.
 */
export function pickBetterPosition(
  prev: Pick<GeolocationPosition, 'timestamp' | 'coords'> | null,
  next: Pick<GeolocationPosition, 'timestamp' | 'coords'>,
): 'next' | 'prev' {
  if (!prev) return 'next';
  const age = next.timestamp - prev.timestamp;
  if (age < 0) return 'prev'; // עדכון שהגיע באיחור מתוך סדר
  if (next.coords.accuracy <= prev.coords.accuracy * 1.5) return 'next';
  return age > 15_000 ? 'next' : 'prev';
}

function errorMessage(err: GeolocationPositionError): { message: string; permission: GeoPermission } {
  if (err.code === err.PERMISSION_DENIED) {
    return {
      message:
        'הגישה למיקום חסומה. אפשרו מיקום לאתר בהגדרות הדפדפן (באייפון: הגדרות ← פרטיות ← שירותי מיקום ← Safari), ואז לחצו "נסה שוב".',
      permission: 'denied',
    };
  }
  if (err.code === err.POSITION_UNAVAILABLE) {
    return { message: 'לא הצלחנו לאתר מיקום כרגע — בדקו ששירותי המיקום (GPS) פועלים במכשיר.', permission: 'prompt' };
  }
  return { message: 'איתור המיקום מתעכב — נסו לצאת למקום פתוח. ממשיכים לנסות ברקע.', permission: 'prompt' };
}

/**
 * Hook מיקום לטלפון ולמחשב: מבקש הרשאה, עוקב ברציפות, וממפה מצבי שגיאה לעברית ברורה.
 *  - שני מעקבים במקביל (GPS מדויק + מיקום גס) ובחירת הטוב מביניהם — בתוך מבנה ה-GPS לבדו לרוב נכשל.
 *  - מתחדש אוטומטית כשחוזרים לאפליקציה (טלפונים משהים מעקב מיקום ברקע) וכשהמעקב "נרדם".
 *  - refresh() — בדיקת מיקום מיידית לפי בקשת המשתמש.
 * לא נדרש חיבור אינטרנט — GPS עצמו עובד Offline (סעיף 45).
 */
export function useGeolocation(active: boolean): GeoState & { requestPermission: () => void; refresh: () => void } {
  const [state, setState] = useState<GeoState>({
    permission: 'unknown',
    position: null,
    error: null,
    loading: false,
  });
  const watchIds = useRef<number[]>([]);
  const positionRef = useRef<GeolocationPosition | null>(null);
  const lastUpdateAt = useRef(0);

  const stop = useCallback(() => {
    for (const id of watchIds.current) navigator.geolocation?.clearWatch(id);
    watchIds.current = [];
  }, []);

  const onPosition = useCallback((position: GeolocationPosition) => {
    lastUpdateAt.current = Date.now();
    const chosen = pickBetterPosition(positionRef.current, position) === 'next' ? position : positionRef.current!;
    positionRef.current = chosen;
    setState({ permission: 'granted', position: chosen, error: null, loading: false });
  }, []);

  const onError = useCallback(
    (err: GeolocationPositionError) => {
      const { message, permission } = errorMessage(err);
      if (permission === 'denied') stop();
      // שגיאת זמן/זמינות בזמן שכבר יש מיקום תקף — לא מציפים את המשתמש; רק כשאין שום מיקום
      setState((s) => (s.position && permission !== 'denied' ? s : { ...s, permission, error: message, loading: false }));
    },
    [stop],
  );

  const start = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setState((s) => ({ ...s, permission: 'unavailable', error: 'GPS אינו זמין במכשיר זה' }));
      return;
    }
    stop();
    lastUpdateAt.current = Date.now();
    setState((s) => ({ ...s, loading: s.position === null, error: s.position ? s.error : null }));
    watchIds.current = [
      navigator.geolocation.watchPosition(onPosition, onError, FINE),
      navigator.geolocation.watchPosition(onPosition, () => undefined, COARSE),
    ];
  }, [onError, onPosition, stop]);

  const refresh = useCallback(() => {
    start();
    // בנוסף למעקב: בקשה חד-פעמית מיידית (מקבלים תשובה מהר גם אם המעקב עדיין מתחמם)
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(onPosition, () => undefined, COARSE);
      navigator.geolocation.getCurrentPosition(onPosition, onError, FINE);
    }
  }, [onError, onPosition, start]);

  // התחלה/עצירה לפי "פעיל"
  useEffect(() => {
    if (!active) return;
    start();
    return () => stop();
  }, [active, start, stop]);

  // חזרה לאפליקציה (מעבר לשונית / נעילת מסך / חזרה מהרקע): מפעילים מחדש מעקב ומבקשים מיקום מיידי
  useEffect(() => {
    if (!active) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, [active, refresh]);

  // "דופק": מעקב שלא סיפק עדכון זמן רב מופעל מחדש
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastUpdateAt.current > STALE_RESTART_MS) start();
    }, 15_000);
    return () => window.clearInterval(id);
  }, [active, start]);

  // מצב ההרשאה בדפדפן (כשנתמך) — אם המשתמש אישר בהגדרות וחזר, מתחילים מיד
  useEffect(() => {
    if (!active || !navigator.permissions?.query) return;
    let status: PermissionStatus | null = null;
    let cancelled = false;
    const apply = () => {
      if (!status || cancelled) return;
      if (status.state === 'denied') {
        setState((s) => ({ ...s, permission: 'denied' }));
      } else if (status.state === 'granted') {
        setState((s) => (s.permission === 'denied' ? { ...s, permission: 'prompt', error: null } : s));
        refresh();
      }
    };
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((s) => {
        if (cancelled) return;
        status = s;
        s.addEventListener('change', apply);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      status?.removeEventListener('change', apply);
    };
  }, [active, refresh]);

  return { ...state, requestPermission: refresh, refresh };
}
